<script lang="ts" setup>
import type {Area} from "#magnolia-layer/types/magnolia-types";
import type {Delivery} from "#magnolia-layer/types/api";
import type {Page} from "~/types/mgnl";
import {useOffsetPagination} from "@vueuse/core";
import usePageQueryRef from "~/composables/usePageQueryRef";
import CategoryFilters from "~/components/CategoryFilters.vue";
import {useMagnoliaFetch} from "#magnolia-layer/composables/use-magnolia-fetch";

const props = defineProps<{
  metadata: Area;
  pageRootLink: string;
  categoryRootLink?: string;
}>();

const pageRef = usePageQueryRef();
const limit = ref(8);
const offset = computed(() => {
  return (pageRef.value - 1) * limit.value; //should be current page
});
const total = computed(() => {
  return content.value?.total ?? 0;
});
const categorySelected = ref("");
const runtimeConfig = useRuntimeConfig();

watch(categorySelected, () => {
  pageRef.value = 1;
});

watch(pageRef, () => {
  if (import.meta.server) return;
  window.scrollTo(0, 0);
});

const params = computed<Record<string, string | number>>(() => ({
  "@ancestor": props.pageRootLink,
  offset: offset.value,
  limit: limit.value,
  ...(categorySelected.value && {categories: categorySelected.value}),
}));

const {data: content, error} = await useMagnoliaFetch<Delivery<Page>>(
  runtimeConfig.public.mgnl.apiPageOverview,
  {params},
);

const offsetPagination = useOffsetPagination({
  page: pageRef,
  total,
  pageSize: limit.value,
});
usePaginationHeadLinks(offsetPagination);
useErrorStatusForwarding(error);

const handleCategoryFilter = (id: string) => {
  categorySelected.value = id;
};
</script>

<template>
  <CategoryFilters
    :category-root-link="categoryRootLink"
    :category-id="categorySelected"
    @handleCategoryFilter="handleCategoryFilter"
    v-if="categoryRootLink" />
  <ArticleGrid :content :offsetPagination :error></ArticleGrid>
</template>

<style lang="scss" scoped>
* {
  overflow-anchor: none;
}
</style>
